(function () {
  'use strict';

  const CONFIG = Object.freeze({
    storageKey: 'frmwrkFitProfileV2',
    sizes: ['S', 'M', 'L', 'XL', '2XL'],
    ranges: { height: [150, 200], weight: [45, 130] },
    chestFormula: { weight: 0.6, height: 0.15, constant: 28 },
    shoulderFormula: { height: 0.245 },
    lengthFormula: { height: 0.405, tolerance: 4 },
    ease: {
      tee: { cmfrt: 20, regular: 12 },
      long_sleeve: { cmfrt: 20, regular: 12 },
      polo: { cmfrt: 14, regular: 8 }
    },
    minimumEase: { tee: 6, long_sleeve: 6, polo: 4 },
    maximumEase: { tee: { cmfrt: 26, regular: 18 }, long_sleeve: { cmfrt: 26, regular: 18 }, polo: { cmfrt: 20, regular: 14 } },
    usualSizeOffset: { cmfrt: 0, regular: -1 },
    scoreWeight: { chest: 0.6, usualSize: 2, shoulder: 0.1, tooTight: 3, tooLoose: 0.8 },
    shoulderTolerance: 2,
    closeResultRatio: 0.1,
    relativeScoreFloor: 1,
    model: { height: 183, size: 'M' }
  });

  const clamp = (value, min, max) => Math.min(max, Math.max(min, Number(value)));
  const normaliseSize = (value) => String(value).trim().toUpperCase().replace('XXL', '2XL');
  const FitFinderElement = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;

  /**
   * Usual size anchors the score, rather than dictating a fixed size-down rule.
   * Height/weight provide a rough chest check against THIS silhouette's chart.
   * Extra penalties discourage excessive room and chest restriction. Shoulder
   * is a soft check only. Height flags length separately and cannot force an
   * otherwise too-roomy size. Among near-equal, comfortable candidates we prefer
   * the smaller size, reflecting the merchant's successful exchange feedback.
   * This is a tunable heuristic, not a model trained on customer measurements.
   */
  function recommendSize(profile, silhouette, spec) {
    if (!CONFIG.sizes.includes(profile.usualSize) || !['cmfrt', 'regular'].includes(profile.fit)
      || !Number.isFinite(profile.height) || !Number.isFinite(profile.weight)
      || !CONFIG.ease[silhouette] || !spec || !spec.sizes) throw new Error('A complete Fit Finder profile and chart are required.');
    const chest = CONFIG.chestFormula.weight * profile.weight
      + CONFIG.chestFormula.height * profile.height
      + CONFIG.chestFormula.constant;
    const shoulder = CONFIG.shoulderFormula.height * profile.height;
    const length = CONFIG.lengthFormula.height * profile.height;
    const targetChest = chest + CONFIG.ease[silhouette][profile.fit];
    const anchor = clamp(CONFIG.sizes.indexOf(profile.usualSize) + CONFIG.usualSizeOffset[profile.fit], 0, CONFIG.sizes.length - 1);
    const minEase = CONFIG.minimumEase[silhouette];
    const maxEase = CONFIG.maximumEase[silhouette][profile.fit];

    const ranked = CONFIG.sizes.filter((size) => spec.sizes[size]).map((size) => {
      const garment = spec.sizes[size];
      const chestDeviation = Math.abs(garment.chest - targetChest);
      const ease = garment.chest - chest;
      const shoulderDeviation = Math.max(0, shoulder - garment.shoulder - CONFIG.shoulderTolerance);
      return {
        size,
        garment,
        score: chestDeviation * CONFIG.scoreWeight.chest
          + Math.abs(CONFIG.sizes.indexOf(size) - anchor) * CONFIG.scoreWeight.usualSize
          + shoulderDeviation * CONFIG.scoreWeight.shoulder
          + Math.max(0, minEase - ease) * CONFIG.scoreWeight.tooTight
          + Math.max(0, ease - maxEase) * CONFIG.scoreWeight.tooLoose,
        comfortable: ease >= minEase && ease <= maxEase,
        chestEase: ease,
        lengthDelta: garment.length - length,
        shoulderDelta: garment.shoulder - shoulder
      };
    }).sort((a, b) => a.score - b.score || CONFIG.sizes.indexOf(a.size) - CONFIG.sizes.indexOf(b.size));

    if (ranked.length < 2) throw new Error('The fit chart needs at least two sizes.');
    const close = (ranked[1].score - ranked[0].score) / Math.max(ranked[0].score, CONFIG.relativeScoreFloor) <= CONFIG.closeResultRatio;
    if (close && ranked[0].comfortable && ranked[1].comfortable
      && CONFIG.sizes.indexOf(ranked[1].size) < CONFIG.sizes.indexOf(ranked[0].size)) {
      [ranked[0], ranked[1]] = [ranked[1], ranked[0]];
    }
    const best = ranked[0];
    const runnerUp = ranked[1];
    return { best, runnerUp, close, confidence: close || !best.comfortable ? 'Medium' : 'High', body: { chest, shoulder, length }, ranked };
  }

  function metricLevel(value, thresholds) {
    let level = 1;
    thresholds.forEach((threshold) => { if (value >= threshold) level += 1; });
    return clamp(level, 1, 5);
  }

  function fitDetails(result, silhouette) {
    const ease = result.best.chestEase;
    const length = result.best.lengthDelta;
    const shoulder = result.best.shoulderDelta;
    const rows = [
      { label: 'Chest', level: metricLevel(ease, [8, 13, 19, 25]), copy: ease < 10 ? 'Close through the chest' : ease < 19 ? 'Easy through the chest' : 'Room to move through the chest' },
      { label: 'Length', level: metricLevel(length, [-4, -1, 2, 5]), copy: length < -CONFIG.lengthFormula.tolerance ? 'May feel short for your height. Check the length guide.' : length > CONFIG.lengthFormula.tolerance ? 'May feel long for your height. Check the length guide.' : 'Within our estimated length range for your height' },
      { label: 'Shoulder', level: metricLevel(shoulder, [0, 3, 6, 9]), copy: shoulder < 1 ? 'A cleaner shoulder line' : shoulder < 6 ? 'Easy through the shoulder' : 'A dropped, roomier shoulder' }
    ];
    if (silhouette === 'long_sleeve') {
      const sleeve = result.best.garment.sleeve;
      rows.push({ label: 'Sleeve', level: metricLevel(sleeve, [63, 64, 65.5, 67]), copy: sleeve < 64 ? 'A neat wrist finish' : sleeve < 66 ? 'Reaches the wrist cleanly' : 'Extra length through the sleeve' });
    }
    return rows;
  }

  function analyticsPublish(eventName, payload) {
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish(eventName, payload || {});
      }
    } catch (error) {}
  }

  class FrmwrkFitFinder extends FitFinderElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;
      if (this.dataset.fontSource !== 'custom') {
        this.style.setProperty('--fit-body-font', getComputedStyle(this.parentElement).fontFamily);
        const productArea = this.closest('.shopify-section') || document;
        const productHeading = productArea.querySelector('h1') || document.querySelector('h1');
        if (productHeading) this.style.setProperty('--fit-heading-font', getComputedStyle(productHeading).fontFamily);
      }
      this.spec = JSON.parse(this.querySelector('[data-fit-spec]').textContent);
      this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
      this.silhouette = this.dataset.silhouette;
      this.dialog = this.querySelector('dialog');
      this.step = 1;
      this.heightUnit = 'cm';
      this.weightUnit = 'kg';
      this.profile = this.loadProfile() || { height: 175, weight: 70, usualSize: null, fit: 'cmfrt' };
      this.bindEvents();
      this.applyProfile();
      this.updateLaunch();
    }

    bindEvents() {
      this.querySelector('.frmwrk-fit__launch').addEventListener('click', () => this.open());
      this.querySelectorAll('[data-close]').forEach((button) => button.addEventListener('click', () => this.close()));
      this.querySelector('[data-next]').addEventListener('click', () => this.next());
      this.querySelector('[data-back]').addEventListener('click', () => this.back());
      this.querySelector('[data-select-size]').addEventListener('click', () => this.selectRecommendedSize());
      this.querySelectorAll('[data-range]').forEach((input) => input.addEventListener('input', (event) => this.rangeChanged(event)));
      this.querySelectorAll('[data-number]').forEach((input) => input.addEventListener('input', () => this.numberChanged(input)));
      this.querySelectorAll('[data-unit-toggle]').forEach((button) => button.addEventListener('click', () => this.toggleUnit(button.dataset.unitToggle)));
      this.dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.close(); });
      this.dialog.addEventListener('keydown', (event) => this.trapFocus(event));
    }

    loadProfile() {
      try {
        const value = JSON.parse(localStorage.getItem(CONFIG.storageKey));
        if (!value || !CONFIG.sizes.includes(value.usualSize)
          || !['cmfrt', 'regular'].includes(value.fit)
          || !Number.isFinite(value.height) || !Number.isFinite(value.weight)) return null;
        return {
          height: clamp(value.height, ...CONFIG.ranges.height),
          weight: clamp(value.weight, ...CONFIG.ranges.weight),
          usualSize: value.usualSize,
          fit: value.fit
        };
      } catch (error) { return null; }
    }

    saveProfile() {
      const { height, weight, usualSize, fit } = this.profile;
      try { localStorage.setItem(CONFIG.storageKey, JSON.stringify({ height, weight, usualSize, fit })); } catch (error) {}
    }

    applyProfile() {
      this.querySelector('[data-range="height"]').value = this.profile.height;
      this.querySelector('[data-number="height"]').value = this.profile.height;
      this.querySelector('[data-range="weight"]').value = this.profile.weight;
      this.querySelector('[data-number="weight"]').value = this.profile.weight;
      this.querySelectorAll('[data-usual-size]').forEach((input) => { input.checked = input.value === this.profile.usualSize; });
      this.querySelectorAll('[data-fit-choice]').forEach((input) => { input.checked = input.value === this.profile.fit; });
      this.syncImperialHeight();
    }

    get blockId() {
      const input = this.querySelector('input[type="radio"]');
      return input ? input.name.replace('usual-size-', '') : '';
    }

    open() {
      this.step = 1;
      this.showStep();
      this.dialog.showModal();
      this.pageScrollY = window.scrollY;
      this.bodyStyles = ['position', 'top', 'width'].map((property) => ({ property, value: document.body.style.getPropertyValue(property), priority: document.body.style.getPropertyPriority(property) }));
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.pageScrollY}px`;
      document.body.style.width = '100%';
      document.body.classList.add('frmwrk-fit-open');
      analyticsPublish('fit_finder_open', { silhouette: this.silhouette });
      requestAnimationFrame(() => this.querySelector('[data-close]').focus());
    }

    close() {
      if (!this.dialog.open) return;
      this.dialog.close();
      document.body.classList.remove('frmwrk-fit-open');
      (this.bodyStyles || []).forEach(({ property, value, priority }) => {
        if (value) document.body.style.setProperty(property, value, priority);
        else document.body.style.removeProperty(property);
      });
      window.scrollTo({ top: this.pageScrollY || 0, behavior: 'instant' });
      this.querySelector('.frmwrk-fit__launch').focus({ preventScroll: true });
    }

    rangeChanged(event) {
      const key = event.target.dataset.range;
      this.profile[key] = Number(event.target.value);
      this.querySelector(`[data-number="${key}"]`).value = this.profile[key];
      if (key === 'height') this.syncImperialHeight();
      if (key === 'weight' && this.weightUnit === 'lbs') this.querySelector('[data-number="weight"]').value = Math.round(this.profile.weight * 2.20462);
    }

    numberChanged(input) {
      const key = input.dataset.number;
      if (key === 'feet' || key === 'inches') {
        const feet = Number(this.querySelector('[data-number="feet"]').value || 0);
        const inches = Number(this.querySelector('[data-number="inches"]').value || 0);
        this.profile.height = clamp(Math.round((feet * 12 + inches) * 2.54), ...CONFIG.ranges.height);
        this.querySelector('[data-range="height"]').value = this.profile.height;
        return;
      }
      const raw = Number(input.value);
      if (!Number.isFinite(raw)) return;
      if (key === 'weight' && this.weightUnit === 'lbs') this.profile.weight = clamp(Math.round(raw / 2.20462), ...CONFIG.ranges.weight);
      else this.profile[key] = clamp(raw, ...CONFIG.ranges[key]);
      this.querySelector(`[data-range="${key}"]`).value = this.profile[key];
    }

    toggleUnit(type) {
      if (type === 'height') {
        this.heightUnit = this.heightUnit === 'cm' ? 'imperial' : 'cm';
        this.querySelector('[data-unit-toggle="height"]').textContent = this.heightUnit === 'cm' ? 'CM' : 'FT / IN';
        this.querySelector('[data-metric-input="height"]').hidden = this.heightUnit !== 'cm';
        this.querySelector('[data-imperial-input="height"]').hidden = this.heightUnit === 'cm';
        this.syncImperialHeight();
      } else {
        this.weightUnit = this.weightUnit === 'kg' ? 'lbs' : 'kg';
        const weightInput = this.querySelector('[data-number="weight"]');
        weightInput.min = this.weightUnit === 'kg' ? CONFIG.ranges.weight[0] : Math.round(CONFIG.ranges.weight[0] * 2.20462);
        weightInput.max = this.weightUnit === 'kg' ? CONFIG.ranges.weight[1] : Math.round(CONFIG.ranges.weight[1] * 2.20462);
        this.querySelector('[data-unit-toggle="weight"]').textContent = this.weightUnit.toUpperCase();
        this.querySelector('[data-weight-unit]').textContent = this.weightUnit;
        this.querySelector('[data-number="weight"]').value = this.weightUnit === 'kg' ? this.profile.weight : Math.round(this.profile.weight * 2.20462);
      }
    }

    syncImperialHeight() {
      const totalInches = Math.round(this.profile.height / 2.54);
      this.querySelector('[data-number="feet"]').value = Math.floor(totalInches / 12);
      this.querySelector('[data-number="inches"]').value = totalInches % 12;
    }

    next() {
      if (this.step === 1) {
        const selected = this.querySelector('[data-usual-size]:checked');
        const error = this.querySelector('[data-profile-error]');
        if (!selected) { error.hidden = false; this.querySelector('[data-usual-size]').focus(); return; }
        error.hidden = true;
        this.profile.usualSize = selected.value;
      }
      if (this.step === 2) this.profile.fit = this.querySelector('[data-fit-choice]:checked').value;
      if (this.step === 3) {
        this.profile.height = clamp(this.profile.height, ...CONFIG.ranges.height);
        this.profile.weight = clamp(this.profile.weight, ...CONFIG.ranges.weight);
        this.saveProfile();
        this.renderResult();
        return;
      }
      this.step += 1;
      this.showStep();
    }

    back() {
      if (this.step > 1) this.step -= 1;
      this.showStep();
    }

    showStep() {
      this.querySelectorAll('[data-step]').forEach((section) => { section.hidden = Number(section.dataset.step) !== this.step; });
      this.querySelector('[data-result]').hidden = true;
      this.querySelector('[data-footer]').hidden = false;
      this.querySelector('[data-back]').hidden = this.step === 1;
      this.querySelector('[data-next]').textContent = this.step === 3 ? 'See my size' : 'Next';
      this.querySelector('[data-progress]').style.width = `${this.step * 33.333}%`;
      this.querySelector('.frmwrk-fit__form').scrollTop = 0;
      const heading = this.querySelector(`[data-step="${this.step}"] h3`);
      heading.setAttribute('tabindex', '-1');
      if (this.dialog.open) heading.focus({ preventScroll: true });
    }

    renderResult() {
      const result = recommendSize(this.profile, this.silhouette, this.spec);
      this.result = result;
      this.querySelectorAll('[data-step]').forEach((section) => { section.hidden = true; });
      this.querySelector('[data-footer]').hidden = true;
      this.querySelector('[data-result]').hidden = false;
      this.querySelector('[data-progress]').style.width = '100%';
      this.querySelector('[data-size]').textContent = result.best.size;
      this.querySelector('[data-confidence]').textContent = `${result.confidence} confidence`;
      const runner = this.querySelector('[data-runner-up]');
      runner.hidden = !result.close;
      const orderedSizes = [result.best.size, result.runnerUp.size].sort((a, b) => Object.keys(this.spec.sizes).indexOf(a) - Object.keys(this.spec.sizes).indexOf(b));
      runner.textContent = result.close ? `Between ${orderedSizes[0]} and ${orderedSizes[1]}. ${result.best.size} is our pick for your ${this.profile.fit === 'cmfrt' ? 'CMFRT' : 'Regular'} fit.` : '';
      this.querySelector('[data-fit-report]').innerHTML = fitDetails(result, this.silhouette).map((row) => `
        <div class="frmwrk-fit__fit-row">
          <span class="frmwrk-fit__fit-label">${row.label}</span>
          <span class="frmwrk-fit__bar" aria-label="${row.level} of 5, close to roomy">${[1,2,3,4,5].map((value) => `<span class="${value === row.level ? 'is-active' : ''}"></span>`).join('')}</span>
          <p class="frmwrk-fit__fit-copy">${row.copy}</p>
        </div>`).join('');
      this.querySelector('[data-model]').textContent = `Model: ${CONFIG.model.height} cm, wears ${CONFIG.model.size}.`;
      const explanation = this.querySelector('[data-recommendation-reason]');
      const change = CONFIG.sizes.indexOf(result.best.size) - CONFIG.sizes.indexOf(this.profile.usualSize);
      explanation.textContent = change < 0 ? `One ${change === -1 ? 'size' : 'or more sizes'} below your usual ${this.profile.usualSize}, based on the room in this cut and your fit preference.` : change > 0 ? `Above your usual ${this.profile.usualSize} to allow enough estimated chest room in this cut.` : `Your usual ${this.profile.usualSize} is our pick for this cut and your fit preference.`;
      const caution = this.querySelector('[data-fit-caution]');
      caution.hidden = result.best.comfortable;
      caution.textContent = result.best.chestEase < CONFIG.minimumEase[this.silhouette] ? 'Even this size may feel close through the chest. Compare the full size guide before choosing.' : 'Even this size may feel roomy through the chest. Compare the full size guide before choosing.';
      this.querySelector('.frmwrk-fit__form').scrollTop = 0;
      this.querySelector('[data-polo-note]').hidden = this.silhouette !== 'polo';
      this.configureSelectButton();
      analyticsPublish('fit_finder_complete', { silhouette: this.silhouette, size: result.best.size, fit: this.profile.fit });
      this.updateLaunch();
    }

    roomierSize(a, b) {
      const order = Object.keys(this.spec.sizes);
      return order.indexOf(a) > order.indexOf(b) ? a : b;
    }

    variantForSize(size) {
      return this.variants.find((variant) => variant.options.some((option) => normaliseSize(option) === normaliseSize(size)));
    }

    configureSelectButton() {
      const primary = this.variantForSize(this.result.best.size);
      const runner = this.variantForSize(this.result.runnerUp.size);
      const button = this.querySelector('[data-select-size]');
      const note = this.querySelector('[data-stock-note]');
      this.selectedResultSize = this.result.best.size;
      note.hidden = true;
      button.disabled = false;
      if (primary && primary.available) button.textContent = `Select ${this.result.best.size}`;
      else if (runner && runner.available) {
        this.selectedResultSize = this.result.runnerUp.size;
        note.hidden = false;
        note.textContent = `${this.result.best.size} is sold out. ${this.result.runnerUp.size} is the closest available size.`;
        button.textContent = `Select ${this.result.runnerUp.size}`;
      } else {
        note.hidden = false;
        note.textContent = `${this.result.best.size} is sold out. The closest alternative is also unavailable.`;
        button.textContent = 'Recommended sizes sold out';
        button.disabled = true;
      }
    }

    selectRecommendedSize() {
      const size = this.selectedResultSize;
      const variant = this.variantForSize(size);
      if (!variant || !variant.available) return;
      const section = this.closest('.shopify-section') || document;
      const sizeOption = Array.from(section.querySelectorAll('input[type="radio"], select')).filter((element) => !element.closest('.frmwrk-fit')).find((element) => {
        if (element.tagName === 'SELECT') return Array.from(element.options).some((option) => normaliseSize(option.textContent) === normaliseSize(size));
        return normaliseSize(element.value) === normaliseSize(size) || normaliseSize(element.dataset.optionValue || '') === normaliseSize(size);
      });
      if (sizeOption) {
        if (sizeOption.tagName === 'SELECT') {
          const option = Array.from(sizeOption.options).find((item) => normaliseSize(item.textContent) === normaliseSize(size));
          if (option) sizeOption.value = option.value;
        } else sizeOption.checked = true;
        sizeOption.dispatchEvent(new Event('change', { bubbles: true }));
      }
      section.querySelectorAll('form[action*="/cart/add"] input[name="id"]').forEach((input) => {
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({ variantId: variant.id }, '', url);
      document.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
      analyticsPublish('fit_finder_select_size', { silhouette: this.silhouette, size, fit: this.profile.fit });
      this.close();
    }

    updateLaunch() {
      const saved = this.loadProfile();
      const label = this.querySelector('[data-launch-text]');
      const edit = this.querySelector('[data-edit-text]');
      if (!saved) { label.textContent = this.dataset.buttonLabel || 'Find your size'; edit.hidden = true; return; }
      const result = recommendSize(saved, this.silhouette, this.spec);
      label.textContent = `Your size in this: ${result.best.size}`;
      edit.hidden = false;
    }

    trapFocus(event) {
      if (event.key !== 'Tab') return;
      const focusable = Array.from(this.dialog.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])')).filter((item) => item.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
  }

  if (typeof customElements !== 'undefined' && !customElements.get('frmwrk-fit-finder')) customElements.define('frmwrk-fit-finder', FrmwrkFitFinder);
  if (typeof module !== 'undefined' && module.exports) module.exports = { CONFIG, recommendSize, fitDetails };
})();
